import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { THANKSGIVING_PICKUP } from "@/data/site";

const DESSERTS = ["Cookies", "Cupcakes", "Cheesecake Slices"];
const OCC = ["Thanksgiving Preorder", "Holiday Preorder", "Birthday", "Celebration", "Shower", "Corporate Event", "Other Special Occasion"];

const field = "w-full bg-transparent border-b border-espresso/25 py-3 text-espresso placeholder:text-mocha/70 focus:outline-none focus:border-gold transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow">{label}</span>
    {children}
  </label>
);

export const InquiryForm = ({ kind = "custom", preset = [], presetOccasion = "" }) => {
  const custom = kind === "custom";
  const [f, setF] = useState({ name: "", email: "", phone: "", occasion: OCC.includes(presetOccasion) ? presetOccasion : "", event_date: presetOccasion === "Thanksgiving Preorder" ? THANKSGIVING_PICKUP.iso : "", guests: "", message: "" });
  const [desserts, setDesserts] = useState(preset);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (d) => setDesserts((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d]));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/inquiries", { ...f, kind, desserts });
      setState("done");
      toast.success(custom ? "Your custom order request has been received." : "Your message has been sent.");
    } catch {
      setState("idle");
      toast.error("Please check your details and try again.");
    }
  };

  if (state === "done")
    return (
      <div data-testid={`${kind}-form-success`} className="py-16">
        <span className="w-14 h-14 rounded-full border border-gold flex items-center justify-center text-gold"><Check className="w-6 h-6" strokeWidth={1.4} /></span>
        <p className="font-display text-4xl mt-8">Thank you, {f.name.split(" ")[0]}.</p>
        <p className="text-mocha mt-4 max-w-md">Your note has arrived at the studio. Lise will be in touch by email soon.</p>
      </div>
    );

  return (
    <form data-testid={`${kind}-form`} onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
      <Field label="Name *"><input data-testid="form-name" required maxLength={120} value={f.name} onChange={set("name")} className={field} /></Field>
      <Field label="Email *"><input data-testid="form-email" required type="email" value={f.email} onChange={set("email")} className={field} /></Field>
      <Field label="Phone"><input data-testid="form-phone" maxLength={40} value={f.phone} onChange={set("phone")} className={field} /></Field>
      {custom && (
        <>
          <Field label="Occasion">
            <select data-testid="form-occasion" value={f.occasion} onChange={(e) => setF({ ...f, occasion: e.target.value, event_date: e.target.value === "Thanksgiving Preorder" ? THANKSGIVING_PICKUP.iso : f.event_date })} className={field}>
              <option value="">Select an occasion</option>
              {OCC.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label={f.occasion.includes("Preorder") ? "Preferred pickup date" : "Event date"}><input data-testid="form-date" type="date" value={f.event_date} onChange={set("event_date")} className={field} />
            {f.occasion === "Thanksgiving Preorder" && <span data-testid="thanksgiving-pickup-note" className="block mt-2 text-xs text-mocha">Thanksgiving pickup is {THANKSGIVING_PICKUP.label}. Last day to order is {THANKSGIVING_PICKUP.orderByLabel}.</span>}
          </Field>
          <Field label="Guest count"><input data-testid="form-guests" maxLength={20} value={f.guests} onChange={set("guests")} className={field} placeholder="Approx." /></Field>
          <div className="sm:col-span-2">
            <span className="eyebrow">Desserts</span>
            <div className="flex flex-wrap gap-3 mt-4">
              {DESSERTS.map((d) => (
                <button type="button" key={d} data-testid={`form-dessert-${d.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => toggle(d)}
                  className={`px-5 py-2.5 rounded-full border text-sm transition-colors duration-300 ${desserts.includes(d) ? "bg-espresso text-cream border-espresso" : "border-espresso/25 hover:border-espresso"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <div className="sm:col-span-2">
        <Field label={custom ? "Tell us about your order *" : "Message *"}>
          <textarea data-testid="form-message" required rows={4} maxLength={4000} value={f.message} onChange={set("message")} className={`${field} resize-none`}
            placeholder={custom ? "Flavours, quantities, colours, inspiration…" : "How can we help?"} />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <button data-testid={`${kind}-form-submit`} disabled={state === "sending"} className="btn btn-dark">
          {state === "sending" ? "Sending…" : custom ? "Send Request" : "Send Message"}
        </button>
      </div>
    </form>
  );
};
